import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// Créer le contexte d'authentification
const AuthContext = createContext();

// Hook personnalisé pour utiliser le contexte d'authentification
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth doit être utilisé à l'intérieur d'un AuthProvider");
  }
  return context;
};

// Provider du contexte d'authentification
export const AuthProvider = ({ children }) => {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const navigate = useNavigate();

  // Effet pour vérifier et maintenir la session utilisateur
  useEffect(() => {
    // Récupérer la session actuelle
    const getSession = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          throw error;
        }
        
        setUser(data.session?.user || null);
      } catch (error) {
        console.error('Erreur lors de la récupération de la session:', error.message);
        setAuthError(error.message);
      } finally {
        setLoading(false);
      }
    };

    getSession();

    // S'abonner aux changements d'état d'authentification
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Événement d\'authentification:', event);
      setUser(session?.user || null);
      setLoading(false);
    });

    // Nettoyer l'abonnement
    return () => {
      if (authListener?.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);

  // Fonction d'inscription
  const signUp = async (email, password, userData = {}) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      
      if (error) {
        throw error;
      }
      
      toast.success(t('auth.accountCreated'));
      return { success: true, data };
    } catch (error) {
      console.error('Erreur d\'inscription:', error.message);
      toast.error(error.message);
      setAuthError(error.message);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  // Fonction de connexion
  const signIn = async (email, password) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        throw error;
      }
      
      toast.success(t('auth.loginSuccess'));
      return { success: true, data };
    } catch (error) {
      console.error('Erreur de connexion:', error.message);
      toast.error(error.message);
      setAuthError(error.message);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  // Fonction de connexion avec OAuth
  const signInWithProvider = async (provider) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      
      if (error) {
        throw error;
      }
      
      return { success: true, data };
    } catch (error) {
      console.error(`Erreur de connexion avec ${provider}:`, error.message);
      toast.error(error.message);
      setAuthError(error.message);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  // Fonction de déconnexion
  const signOut = async () => {
    try {
      setLoading(true);
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw error;
      }
      
      toast.success(t('auth.logoutSuccess'));
      return { success: true };
    } catch (error) {
      console.error('Erreur de déconnexion:', error.message);
      toast.error(error.message);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  // Fonction de réinitialisation de mot de passe
  const resetPassword = async (email) => {
    try {
      setLoading(true);
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      
      if (error) {
        throw error;
      }
      
      toast.success(t('auth.resetPasswordSent'));
      return { success: true };
    } catch (error) {
      console.error('Erreur de réinitialisation de mot de passe:', error.message);
      toast.error(error.message);
      setAuthError(error.message);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour mettre à jour le mot de passe
  const updatePassword = async (newPassword) => {
    try {
      setLoading(true);
      
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      
      if (error) {
        throw error;
      }
      
      toast.success(t('profile.passwordUpdated'));
      return { success: true };
    } catch (error) {
      console.error('Erreur de mise à jour du mot de passe:', error.message);
      toast.error(error.message);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  // Renvoyer l'email de confirmation
  const resendConfirmationEmail = async (email) => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });

      if (error) {
        console.error('Erreur lors du renvoi de l\'email de confirmation:', error);
        toast.error(t('errors.resendConfirmationFailed'));
        return { success: false, error };
      }

      toast.success(t('auth.confirmationEmailSent'));
      return { success: true };
    } catch (error) {
      console.error('Erreur lors du renvoi de l\'email de confirmation:', error);
      toast.error(t('errors.resendConfirmationFailed'));
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  // Valeur du contexte à exposer
  const value = {
    user,
    loading,
    authError,
    signUp,
    signIn,
    signInWithProvider,
    signOut,
    resetPassword,
    updatePassword,
    resendConfirmationEmail,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
